import { useState } from "react";
import { Check, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { trackCTA } from "@/lib/analytics";

const PAID_TIERS = [
  {
    id: "starter",
    name: "Starter",
    monthly: 29.99,
    annual: 287.90,
    credits: "500",
    orderLimit: "Up to 500 orders / mo",
    blurb: "For stores finding their first creators.",
    features: [
      "500 detection credits / month",
      "Up to 500 orders scanned / mo",
      "Instagram + TikTok matching",
      "Influence scoring (0–100)",
      "CSV export",
      "Email support",
    ],
    highlighted: false,
  },
  {
    id: "growth",
    name: "Growth",
    monthly: 69.99,
    annual: 671.90,
    credits: "2,000",
    orderLimit: "Up to 2,000 orders / mo",
    blurb: "For brands running creator campaigns.",
    features: [
      "2,000 detection credits / month",
      "Up to 2,000 orders scanned / mo",
      "4-platform social matching (IG, TikTok, YT, X)",
      "Klaviyo, Mailchimp & Gorgias sync",
      "UTM tracking per creator",
      "Lookalike discovery",
      "Priority support",
    ],
    highlighted: true,
    badge: "Most popular",
  },
  {
    id: "pro",
    name: "Pro",
    monthly: 174.99,
    annual: 1679.90,
    credits: "5,000",
    orderLimit: "Up to 5,000 orders / mo",
    blurb: "For scaling brands with active creator programs.",
    features: [
      "5,000 detection credits / month",
      "Up to 5,000 orders scanned / mo",
      "All Growth features",
      "Advanced lookalike discovery",
      "Custom influence weighting",
      "API access (read-only)",
      "Priority support + onboarding",
    ],
    highlighted: false,
  },
  {
    id: "scale",
    name: "Scale",
    monthly: 349.99,
    annual: 3359.90,
    credits: "10,000",
    orderLimit: "Up to 10,000 orders / mo",
    blurb: "For 8-figure stores with creator teams.",
    features: [
      "10,000 detection credits / month",
      "Up to 10,000 orders scanned / mo",
      "All Pro features",
      "Dedicated match verification",
      "Full API + webhooks",
      "Shopify Plus / Adobe Commerce features",
      "Dedicated CSM",
    ],
    highlighted: false,
  },
];

export default function Pricing() {
  const [annual, setAnnual] = useState(false);

  return (
    <section id="pricing" data-testid="pricing-section" className="relative py-24 md:py-32 bg-[#09090B]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Social proof banner */}
        <div
          data-testid="pricing-social-proof"
          className="mx-auto max-w-3xl mb-12 flex items-center justify-center gap-3 px-5 py-3 border border-[#D4FF00]/30 bg-[#D4FF00]/5 rounded-sm"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-[#D4FF00] animate-pulse flex-shrink-0" />
          <p className="text-xs md:text-sm text-white/80 text-center font-medium">
            Most merchants find their first influencer in{" "}
            <span className="text-[#D4FF00] font-semibold">24 seconds or less</span>.
          </p>
        </div>

        <div className="text-center max-w-3xl mx-auto">
          <p className="text-[11px] tracking-[0.3em] uppercase text-[#D4FF00] font-semibold">
            Pricing
          </p>
          <h2 className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl mt-4">
            Pay less than <br /> one influencer fee.
          </h2>
          <p className="mt-6 text-base md:text-lg text-[#A1A1AA]">
            Start with a 7-day free trial. Upgrade, downgrade, or cancel anytime.
          </p>

          {/* Billing toggle */}
          <div
            data-testid="billing-toggle-wrap"
            className="mt-10 inline-flex items-center gap-1 p-1 border border-white/10 rounded-sm bg-white/[0.03]"
          >
            <button
              type="button"
              data-testid="billing-toggle-monthly"
              onClick={() => setAnnual(false)}
              className={`px-5 py-2 rounded-sm text-xs uppercase tracking-[0.18em] font-display transition-colors ${
                !annual ? "bg-[#D4FF00] text-black" : "text-white/60 hover:text-white"
              }`}
            >
              Monthly
            </button>
            <button
              type="button"
              data-testid="billing-toggle-annual"
              onClick={() => setAnnual(true)}
              className={`px-5 py-2 rounded-sm text-xs uppercase tracking-[0.18em] font-display transition-colors flex items-center gap-2 ${
                annual ? "bg-[#D4FF00] text-black" : "text-white/60 hover:text-white"
              }`}
            >
              Annual
              <span className={`text-[9px] px-1.5 py-0.5 rounded-sm ${annual ? "bg-black text-[#D4FF00]" : "bg-[#D4FF00]/15 text-[#D4FF00]"}`}>
                Save 20%
              </span>
            </button>
          </div>
        </div>

        {/* Free trial highlight strip */}
        <div
          data-testid="pricing-free-trial"
          className="mt-14 mb-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 p-6 md:p-7 border border-white/10 rounded-sm bg-gradient-to-r from-[#0E0E10] to-[#0B0B0D]"
        >
          <div className="flex items-start md:items-center gap-5">
            <div className="hidden md:flex w-12 h-12 items-center justify-center bg-[#D4FF00]/10 border border-[#D4FF00]/30 rounded-sm font-display text-[#D4FF00] text-lg">
              $0
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] tracking-[0.22em] uppercase text-[#D4FF00] font-semibold">
                  Free Trial
                </span>
                <span className="text-[10px] tracking-[0.18em] uppercase text-white/50">
                  · 7 days
                </span>
              </div>
              <p className="mt-1 font-display uppercase text-xl md:text-2xl tracking-tight">
                Try it free · 50 credits, no card required
              </p>
              <p className="mt-1 text-xs md:text-sm text-[#A1A1AA]">
                Auto-converts to your chosen plan after day 7. Cancel before — pay nothing.
              </p>
            </div>
          </div>
          <Button
            data-testid="pricing-cta-free-trial"
            onClick={() => {
              trackCTA("start_free_trial", "pricing_banner", { tier: "free_trial" });
              window.open("https://app.influencedetector.com/", "_blank", "noopener,noreferrer");
            }}
            className="btn-lime rounded-sm h-12 px-6 text-xs hover:text-black w-full md:w-auto"
          >
            Start free trial
            <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2.5} />
          </Button>
        </div>

        {/* 4 paid tiers */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {PAID_TIERS.map((t) => {
            const price = annual ? t.annual : t.monthly;
            const period = annual ? "/ year" : "/ month";
            const monthlyEffective = annual ? (t.annual / 12).toFixed(2) : null;
            const yearlyAtMonthly = (t.monthly * 12).toFixed(2);
            const savings = (t.monthly * 12 - t.annual).toFixed(2);
            return (
              <div
                key={t.id}
                data-testid={`pricing-tier-${t.id}`}
                className={`relative p-7 rounded-sm border transition-all duration-300 flex flex-col ${
                  t.highlighted
                    ? "bg-[#D4FF00] text-black border-[#D4FF00] lg:-translate-y-4 shadow-[8px_8px_0_0_#000]"
                    : "bg-[#0E0E10] text-white border-white/10 hover:border-white/25"
                }`}
              >
                {t.highlighted && t.badge && (
                  <div className="absolute -top-3 left-7 bg-black text-[#D4FF00] text-[10px] tracking-[0.22em] uppercase font-bold px-3 py-1.5 rounded-sm">
                    {t.badge}
                  </div>
                )}

                <div className={`text-xs tracking-[0.22em] uppercase font-semibold ${t.highlighted ? "text-black/70" : "text-white/50"}`}>
                  {t.name}
                </div>

                <div className="mt-4">
                  {annual && (
                    <div className="flex items-center gap-2 mb-1.5">
                      <span
                        className={`text-sm line-through ${t.highlighted ? "text-black/45" : "text-white/35"}`}
                        data-testid={`pricing-${t.id}-strikethrough`}
                      >
                        ${Number(yearlyAtMonthly).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                      <span
                        data-testid={`pricing-${t.id}-discount-badge`}
                        className={`px-2 py-0.5 rounded-sm text-[10px] tracking-[0.14em] uppercase font-display ${
                          t.highlighted ? "bg-black text-[#D4FF00]" : "bg-[#D4FF00] text-black"
                        }`}
                      >
                        20% off
                      </span>
                    </div>
                  )}
                  <div className="flex items-baseline gap-1.5 flex-wrap">
                    <span className="font-display text-5xl">
                      ${price.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                    <span className={`text-sm ${t.highlighted ? "text-black/70" : "text-white/50"}`}>{period}</span>
                  </div>
                  {annual && (
                    <div className="mt-2 space-y-0.5">
                      <p
                        data-testid={`pricing-${t.id}-savings`}
                        className={`text-xs font-semibold ${t.highlighted ? "text-black" : "text-[#D4FF00]"}`}
                      >
                        You save ${Number(savings).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/year
                      </p>
                      <p className={`text-[11px] ${t.highlighted ? "text-black/65" : "text-[#A1A1AA]"}`}>
                        ≈ ${monthlyEffective}/mo · 2 months free
                      </p>
                    </div>
                  )}
                </div>

                <p className={`mt-3 text-sm ${t.highlighted ? "text-black/75" : "text-[#A1A1AA]"}`}>
                  {t.blurb}
                </p>

                <Button
                  data-testid={`pricing-cta-${t.id}`}
                  onClick={() => {
                    trackCTA("start_7day_trial", "pricing_tier", { tier: t.id, billing: annual ? "annual" : "monthly", price: annual ? t.annual : t.monthly });
                    window.open("https://app.influencedetector.com/", "_blank", "noopener,noreferrer");
                  }}
                  className={`w-full mt-7 rounded-sm h-12 font-display uppercase tracking-wider text-xs ${
                    t.highlighted
                      ? "bg-black text-[#D4FF00] hover:bg-[#0E0E10] hover:text-[#D4FF00]"
                      : "btn-lime hover:text-black"
                  }`}
                >
                  Start 7-day trial
                </Button>
                <p
                  data-testid={`pricing-trial-note-${t.id}`}
                  className={`mt-3 text-center text-[11px] ${t.highlighted ? "text-black/70" : "text-[#A1A1AA]"}`}
                >
                  First 7 days free · cancel anytime
                </p>

                <div className={`mt-7 pt-5 border-t flex items-center justify-between ${t.highlighted ? "border-black/15" : "border-white/10"}`}>
                  <span className={`text-[10px] tracking-[0.18em] uppercase ${t.highlighted ? "text-black/60" : "text-white/45"}`}>
                    Credits / month
                  </span>
                  <span className={`font-display text-lg ${t.highlighted ? "text-black" : "text-[#D4FF00]"}`}>
                    {t.credits}
                  </span>
                </div>
                <div className={`pt-3 pb-1 flex items-center justify-between ${t.highlighted ? "" : ""}`}>
                  <span className={`text-[10px] tracking-[0.18em] uppercase ${t.highlighted ? "text-black/60" : "text-white/45"}`}>
                    Order limit
                  </span>
                  <span className={`text-xs font-mono-id ${t.highlighted ? "text-black/85" : "text-white/80"}`}>
                    {t.orderLimit.replace("Up to ", "").replace(" / mo", "/mo")}
                  </span>
                </div>

                <ul className="mt-5 space-y-2.5 flex-1">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-[13px] leading-relaxed">
                      <Check
                        className={`w-3.5 h-3.5 mt-1 flex-shrink-0 ${t.highlighted ? "text-black" : "text-[#D4FF00]"}`}
                        strokeWidth={3}
                      />
                      <span className={t.highlighted ? "text-black/85" : "text-white/80"}>
                        {f}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Enterprise tier */}
        <div
          data-testid="pricing-tier-enterprise"
          className="mt-8 p-8 md:p-10 border border-white/10 rounded-sm bg-[#0B0B0D] flex flex-col md:flex-row items-start md:items-center justify-between gap-6"
        >
          <div className="flex-1">
            <div className="text-xs tracking-[0.22em] uppercase text-white/50 font-semibold">
              Enterprise
            </div>
            <h3 className="font-display uppercase text-3xl md:text-4xl mt-2 tracking-tight">
              10,000+ orders / mo · custom credits
            </h3>
            <p className="mt-3 text-sm md:text-base text-[#A1A1AA] max-w-2xl">
              For high-volume merchants, agencies managing multiple stores, and Shopify Plus brands needing custom SLAs, dedicated infrastructure, or white-glove onboarding.
            </p>
            <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-xs text-[#A1A1AA]">
              <span className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#D4FF00]" strokeWidth={3} /> Custom credit allowance</span>
              <span className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#D4FF00]" strokeWidth={3} /> Volume discounts</span>
              <span className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#D4FF00]" strokeWidth={3} /> Custom SLA & DPA</span>
              <span className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#D4FF00]" strokeWidth={3} /> Dedicated CSM</span>
            </div>
          </div>
          <Button
            data-testid="pricing-cta-enterprise"
            onClick={() => {
              trackCTA("contact_sales", "pricing_enterprise", { tier: "enterprise" });
              window.open("mailto:sales@influencedetector.com?subject=Enterprise%20Inquiry", "_blank");
            }}
            className="btn-lime rounded-sm h-12 px-7 text-xs hover:text-black w-full md:w-auto whitespace-nowrap"
          >
            Contact sales
            <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2.5} />
          </Button>
        </div>

        <p className="text-center text-xs text-[#A1A1AA] mt-10 max-w-2xl mx-auto">
          Annual plans grant the same monthly credit allowance — credits reset each calendar month and do not stack across the year. All plans billed in USD. Switch or cancel anytime from your dashboard.
        </p>
      </div>
    </section>
  );
}
